/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ChangeEvent, useState } from 'react'
import { captureException } from '@sentry/browser'
import { Controller, useFormContext } from 'react-hook-form'
import { isEmpty } from 'lodash'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

import { FormControl } from 'components'
import skuUploadImage from 'assets/svg/icon-upload-image-gray.svg'
import { IconCloseCircle } from 'components/Icons'
import { getImageFileName, resizeImage } from 'helpers/common'

import ReactTooltip from 'react-tooltip'

import type { IFileList } from 'features/StockKeepingUnit'

interface StockKeepingUnitUploadProps {
  setFileList: (list: IFileList[]) => void
  fileList: IFileList[] | undefined
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
function StockKeepingUnitUpload(
  props: StockKeepingUnitUploadProps,
): JSX.Element {
  const { setFileList, fileList } = props
  const { control, setValue, watch, trigger } = useFormContext()
  const [isMultiple] = useState<boolean>(true)

  const [, setImageChecking] = useState<boolean>(false)
  const [tooltip, setTooltip] = useState<number | undefined>(0)
  const [isFetchImgMain, setIsFetchImgMain] = useState(false)

  const { t } = useTranslation(['sku'])

  const files = watch('files')

  const base = 'f-stock-keeping-unit__form'
  const baseUpload = 'f-stock-keeping-unit-upload'
  const basePreview = 'f-stock-keeping-unit-preview-image'
  const previewList: IFileList[] = !isEmpty(fileList) ? fileList : files || []

  const handleFirstUploadImage = () => {
    return (
      <div className={`${base}__upload-images-layout`}>
        <div className={`${basePreview}__product-image-header`}>
          <span className={`${basePreview}__product-image-count-label`}>
            {t('sku:productImage.title')}
          </span>
          <span className={`${basePreview}__product-image-count-label`}>
            {t('sku:productImage.count')}
            {!isEmpty(previewList) ? previewList.length : 0}/5
          </span>
        </div>
        <div className={`${baseUpload}__first-upload-image`}>
          <img
            className={`${baseUpload}__icon-first-upload-image`}
            src={skuUploadImage}
          />
          <div className={`${baseUpload}__content-first-upload-image`}>
            <span className={`${baseUpload}__label-first-upload-image`}>
              {t('sku:uploadProductImage.sku')}
            </span>
            <span className={`${baseUpload}__description-first-upload-image`}>
              {t('sku:uploadProductImage.sku.description')}
            </span>
          </div>
        </div>
      </div>
    )
  }

  const handleUploadImageRow = () => {
    return (
      <>
        <a data-for='tooltip-upload-image' data-tip>
          <div className={`${baseUpload}__container-upload-image-row`}>
            <div className={`${baseUpload}__upload-image-row`}>
              <img
                className={`${baseUpload}__icon-upload-image-row`}
                src={skuUploadImage}
              />
              <div className={`${baseUpload}__content-upload-image-row`}>
                <span className={`${baseUpload}__label-upload-image-row`}>
                  {t('sku:uploadProductImage.sku')}
                </span>
              </div>
            </div>
          </div>
          <ReactTooltip
            className={`${baseUpload}__tools`}
            id='tooltip-upload-image'
            place='right'
            type='dark'
            effect='solid'
            backgroundColor='#3B4050'
            offset={{
              top: 32,
              left: 0,
            }}
          >
            <div className={`${baseUpload}__container-content-tooltip`}>
              <span className='label-top-content-tooltip'>
                {t('sku:uploadProductImage.sku')}
              </span>
              <span className='label-bottom-content-tooltip'>
                {t('sku:uploadProductImage.sku.description')}
              </span>
            </div>
          </ReactTooltip>
        </a>
      </>
    )
  }

  const handleSetDefaultThumbnail = (list: IFileList[], path: string) => {
    return list
      .filter((item) => path === item.path)
      .map((item) => {
        return { ...item, isThumbnail: true }
      })
  }

  const handleRemoveImage = (path: string) => {
    let newFiles = previewList?.filter((item) => path !== item.path)

    const defaultThumbnail = newFiles.find((item) => item.isThumbnail === true)

    if (isEmpty(defaultThumbnail) && !isEmpty(newFiles)) {
      const thumbnailList = handleSetDefaultThumbnail(
        newFiles,
        newFiles[0].path,
      )
      const otherList = newFiles.filter((item, index) => index !== 0)

      newFiles = [...thumbnailList, ...otherList]
    }

    setFileList(newFiles)
    setValue('files', newFiles, { shouldDirty: true })
  }

  const setFileMultiple = (listFile: IFileList[]) => {
    const list = [...(previewList || []), ...listFile].slice(0, 5)

    setFileList(list)
    setValue('files', list, { shouldDirty: true })
  }

  const handleChangeMultiple = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files || []
    const fileLength: number = files?.length || 0
    const listFile: IFileList[] = []

    if (files) {
      for (let i = 0; i < fileLength; i++) {
        const file = files[i]

        const originalSize = file.size
        const targetSize = 204800 // 200kb

        const isMain = Boolean(
          previewList?.find((item) => item.isThumbnail === true),
        )

        const setIndexThumbnail = i === 0 ? true : false
        const isThumbnail = isMain ? false : setIndexThumbnail

        if (originalSize > targetSize) {
          resizeImage(file, 720, 960).then(
            (blob) => {
              listFile.push({
                path: URL.createObjectURL(blob),
                file: blob,
                isThumbnail,
              })

              setFileMultiple(listFile)
            },
            (error) => {
              captureException(error)
            },
          )
        } else {
          file.arrayBuffer().then((arrayBuffer) => {
            const blob = new Blob([new Uint8Array(arrayBuffer)], {
              type: file.type,
            })

            listFile.push({
              path: URL.createObjectURL(blob),
              file: blob,
              isThumbnail,
            })

            setFileMultiple(listFile)
          })
        }
      }
    }
  }

  const handleOnClickImgMain = (index: number) => {
    const temp = previewList[0]
    previewList[0] = previewList[index]
    previewList[index] = temp

    setIsFetchImgMain(!isFetchImgMain)
  }

  const previewImageList = () => {
    if (!fileList) {
      return
    }
    return previewList?.map((item, index) => {
      const leaveImg = -1
      if (index === 0) {
        return (
          <React.Fragment key={`previewList-${index}`}>
            <div className={`${basePreview}__product-image-header`}>
              <span className={`${basePreview}__product-image-count-label`}>
                {t('sku:productImage.title')}
              </span>
              <span className={`${basePreview}__product-image-count-label`}>
                {t('sku:productImage.count')}
                {previewList?.length != 0 ? previewList?.length : 0}/5
              </span>
            </div>
            <div className={`${baseUpload}__first-upload-image`}>
              <IconCloseCircle
                className={`${baseUpload}__cancel-upload`}
                onClick={() => handleRemoveImage(item.path)}
                variants='md'
              />
              <div
                style={{
                  backgroundImage: `url(${
                    item.file ? item.path : getImageFileName(item.path)
                  })`,
                  width: 325,
                  height: 244,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  borderRadius: 8,
                }}
              />
              <div className={`${baseUpload}__label-first-image`}>
                {t('sku:thumbnail')}
              </div>
            </div>
          </React.Fragment>
        )
      } else if (index > 0 && index < 5) {
        return (
          <div
            className={`${baseUpload}__container-image-row`}
            onMouseEnter={() => setTooltip(index)}
            onMouseLeave={() => setTooltip(leaveImg)}
            key={`previewList-${index}`}
          >
            <IconCloseCircle
              className={clsx(
                index === 4
                  ? `${baseUpload}__remove-image-row-upload-last-img`
                  : `${baseUpload}__remove-image-row-upload`,
              )}
              onClick={() => handleRemoveImage(item.path)}
              variants='sm'
            />
            <div
              style={{
                backgroundImage: `url(${
                  item.file ? item.path : getImageFileName(item.path)
                })`,
                width: 70,
                height: 70,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                borderRadius: 8,
                marginTop: 15,
                marginRight: index === 4 ? 0 : 15,
              }}
            />
            {tooltip === index && (
              <div
                onClick={() => handleOnClickImgMain(index)}
                className={`${baseUpload}__label-choose-image`}
              >
                {t('sku:setThumbnail')}
              </div>
            )}
          </div>
        )
      }
    })
  }

  return (
    <div className={`${base}__fields`}>
      <FormControl>
        <Controller
          control={control}
          name='files'
          render={() => (
            <div className={`${base}__upload-images-layout`}>
              {previewImageList()}
              {previewList?.length < 5 && (
                <label htmlFor='image-input'>
                  <input
                    data-testid='image-input'
                    id='image-input'
                    type='file'
                    accept='image/jpeg, image/jpg, image/png'
                    onBlur={async () => {
                      setImageChecking(true)
                      await trigger()
                      setImageChecking(false)
                    }}
                    onChange={(e) => {
                      handleChangeMultiple(e)
                    }}
                    onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                      const valueClick = e.target as HTMLInputElement
                      valueClick.value = ''
                    }}
                    multiple={isMultiple}
                    hidden
                  />
                  {!isEmpty(previewList) && previewList?.length > 0
                    ? handleUploadImageRow()
                    : handleFirstUploadImage()}
                </label>
              )}
            </div>
          )}
        />
      </FormControl>
    </div>
  )
}

export default StockKeepingUnitUpload
